/**
 * Gift With Purchase controller.
 * Owns tier resolution, cart attributes, and gift add/swap/remove.
 * Price is never set here — a Shopify Function zeroes the gift at checkout.
 */
const GWP_RIVO_CART_EVENTS = [
  'rivo-accounts:cart:add',
  'rivo-accounts:items:updated',
  'rivo-accounts:items:removed',
];

if (!customElements.get('gwp-controller')) {
  customElements.define(
    'gwp-controller',
    class GwpController extends HTMLElement {
      queue = Promise.resolve();
      syncing = false;
      cartUnsubscriber = undefined;
      rivoSyncTimer = 0;
      ignoreRivoUntil = 0;
      /** @type {Record<number, boolean>} */
      tierAvailability = {};
      /** @type {Record<number, boolean>} */
      giftAvailability = {};
      availabilityFetchedAt = 0;
      preparingCheckout = false;
      checkoutPassThrough = false;
      lastPublishedTier = null;

      constructor() {
        super();
        this.onCartPubSub = this.onCartPubSub.bind(this);
        this.onCartUpdated = this.onCartUpdated.bind(this);
        this.onRivoCartChange = this.onRivoCartChange.bind(this);
        this.onCheckoutClick = this.onCheckoutClick.bind(this);
        this.onCheckoutSubmit = this.onCheckoutSubmit.bind(this);
      }

      connectedCallback() {
        this.config = this.readConfig();
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartPubSub);
        }
        document.addEventListener('cart:updated', this.onCartUpdated);
        document.addEventListener('click', this.onCheckoutClick, true);
        document.addEventListener('submit', this.onCheckoutSubmit, true);

        for (const name of GWP_RIVO_CART_EVENTS) {
          document.addEventListener(name, this.onRivoCartChange);
        }

        if (this.config.enabled) {
          this.syncGift(undefined, { skipRender: true });
        }
      }

      disconnectedCallback() {
        this.cartUnsubscriber?.();
        document.removeEventListener('cart:updated', this.onCartUpdated);
        document.removeEventListener('click', this.onCheckoutClick, true);
        document.removeEventListener('submit', this.onCheckoutSubmit, true);
        this.clearRivoSyncTimer();

        for (const name of GWP_RIVO_CART_EVENTS) {
          document.removeEventListener(name, this.onRivoCartChange);
        }
      }

      /**
       * @returns {object}
       */
      readConfig() {
        const node = this.querySelector('#GwpConfig');
        if (!node?.textContent) {
          return { enabled: false, tiers: [], copy: {}, status: 'none', acceptedTier: 0, seenTiers: '' };
        }

        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error('GWP: failed to parse config', error);
          return { enabled: false, tiers: [], copy: {}, status: 'none', acceptedTier: 0, seenTiers: '' };
        }
      }

      /**
       * Called from the cart.js mutation chain (skipRender) and from other cart paths.
       * @param {object} [cartState]
       * @param {{ skipRender?: boolean }} [options]
       * @returns {Promise<object>}
       */
      syncGift(cartState, options = {}) {
        return this.enqueue(() => this.runSync(cartState, options));
      }

      /**
       * Accept the current earned tier and restore its complete gift set.
       * @returns {Promise<object>}
       */
      acceptGift() {
        return this.enqueue(() => this.runSync(undefined, {
          skipRender: false,
          forceStatus: 'accepted',
        }));
      }

      /**
       * Decline the program. Gift is removed and never auto-prompted again.
       * @returns {Promise<object>}
       */
      async declineGift() {
        const cart = await this.fetchCart();
        return this.syncGift(cart, { skipRender: false, forceStatus: 'declined' });
      }

      enqueue(task) {
        this.queue = this.queue.then(task, task);
        return this.queue;
      }

      onCartPubSub(event) {
        if (event?.source === 'cart-items') return;
        if (this.syncing) return;

        this.syncGift(event?.cartData, { skipRender: false });
      }

      onCartUpdated(event) {
        if (this.syncing) return;

        this.syncGift(event?.detail, { skipRender: false });
      }

      /**
       * Rivo Favorites / account widget adds without theme cartUpdate.
       * Ignore event.detail (not a Shopify cart). Debounce so add + updated
       * fire once after Rivo finishes writing the cart.
       * Skip while we are syncing, and for a short window after our own
       * cart writes, so Rivo echoing those writes cannot loop.
       */
      onRivoCartChange() {
        if (!this.config?.enabled || this.syncing) return;
        if (Date.now() < this.ignoreRivoUntil) return;

        this.clearRivoSyncTimer();
        this.rivoSyncTimer = window.setTimeout(() => {
          this.rivoSyncTimer = 0;
          if (!this.isConnected || !this.config?.enabled || this.syncing) return;
          if (Date.now() < this.ignoreRivoUntil) return;
          this.syncGift(undefined, { skipRender: false });
        }, 300);
      }

      clearRivoSyncTimer() {
        window.clearTimeout(this.rivoSyncTimer);
        this.rivoSyncTimer = 0;
      }

      quietRivoEcho() {
        this.clearRivoSyncTimer();
        this.ignoreRivoUntil = Date.now() + 800;
      }

      /**
       * Cart Checkout / Shop Pay, including Loop's .cart__checkout-button selector.
       * @param {EventTarget | null} target
       * @returns {{ kind: 'checkout' | 'shop_pay', node: Element, href?: string } | null}
       */
      resolveCheckoutTrigger(target) {
        if (!(target instanceof Element)) return null;

        const shopPay = target.closest('.cart__shop-pay-button, .pf06-checkout-text');
        if (shopPay) {
          return { kind: 'shop_pay', node: shopPay, href: '/checkout?payment=shop_pay' };
        }

        const checkout = target.closest('.cart__checkout-button, [name="checkout"]');
        if (checkout) {
          return { kind: 'checkout', node: checkout };
        }

        return null;
      }

      /**
       * @param {Event} event
       */
      onCheckoutClick(event) {
        if (this.checkoutPassThrough) return;
        if (!this.config?.enabled) return;

        const trigger = this.resolveCheckoutTrigger(event.target);
        if (!trigger) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.preparingCheckout) return;

        this.runCheckoutPrepare(trigger);
      }

      /**
       * @param {Event} event
       */
      onCheckoutSubmit(event) {
        if (this.checkoutPassThrough) return;
        if (!this.config?.enabled) return;

        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.id !== 'cart' && form.id !== 'CartDrawer-Form' && form.id !== 'cart-notification-form') {
          return;
        }

        const submitter = event.submitter;
        if (
          submitter &&
          submitter.getAttribute('name') !== 'checkout' &&
          !submitter.classList.contains('cart__checkout-button')
        ) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.preparingCheckout) return;

        this.runCheckoutPrepare({
          kind: 'checkout',
          node: submitter || form.querySelector('.cart__checkout-button, [name="checkout"]') || form,
        });
      }

      /**
       * @param {{ kind: 'checkout' | 'shop_pay', node: Element, href?: string }} trigger
       */
      async runCheckoutPrepare(trigger) {
        if (this.preparingCheckout) return;

        this.preparingCheckout = true;
        this.setCheckoutBusy(true);

        try {
          await this.prepareGiftForCheckout();
        } catch (error) {
          console.error('GWP: checkout prepare failed', error);
        }

        this.checkoutPassThrough = true;
        this.preparingCheckout = false;
        this.setCheckoutBusy(false);
        this.continueCheckout(trigger);
      }

      /**
       * Live inventory, then walk down / remove before leaving the storefront.
       * Does not run on Shopify Checkout — that remaining race is Approach B.
       * @returns {Promise<void>}
       */
      async prepareGiftForCheckout() {
        if (!this.config?.enabled) return;

        await this.refreshTierAvailability();
        await this.syncGift(undefined, { skipRender: false });
      }

      /**
       * Live stock for walk-down. Cached briefly so wrap/monogram cart
       * updates do not refetch every configured gift on every click.
       * @returns {Promise<void>}
       */
      async ensureLiveAvailability() {
        if (this.availabilityFetchedAt && Date.now() - this.availabilityFetchedAt < 5000) {
          this.applyLiveAvailability();
          return;
        }

        await this.refreshTierAvailability();
      }

      /**
       * @returns {Promise<void>}
       */
      async refreshTierAvailability() {
        const products = new Map();
        const next = { ...this.giftAvailability };

        for (const tier of this.config.tiers || []) {
          for (const gift of tier.gifts || []) {
            const handle = this.giftHandle(gift);
            if (!handle || !gift.variantId) {
              next[gift.variantId] = false;
              continue;
            }
            if (products.has(handle)) continue;

            products.set(handle, (async () => {
              try {
                const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
                  cache: 'no-store',
                });
                if (response.status === 404) return { variants: [] };
                if (!response.ok) return null;
                return await response.json();
              } catch (error) {
                console.error('GWP: availability fetch failed', handle, error);
                return null;
              }
            })());
          }
        }

        await Promise.all(products.values());

        for (const tier of this.config.tiers || []) {
          for (const gift of tier.gifts || []) {
            const product = await products.get(this.giftHandle(gift));
            if (!product) continue;
            const variant = (product.variants || []).find(
              (entry) => Number(entry.id) === Number(gift.variantId),
            );
            next[gift.variantId] = Boolean(variant?.available);
          }
        }

        this.giftAvailability = next;
        this.tierAvailability = {};
        this.availabilityFetchedAt = Date.now();
        this.applyLiveAvailability();
      }

      applyLiveAvailability() {
        for (const tier of this.config.tiers || []) {
          for (const gift of tier.gifts || []) {
            if (!Object.prototype.hasOwnProperty.call(this.giftAvailability, gift.variantId)) continue;
            gift.available = this.giftAvailability[gift.variantId];
          }
          tier.available = Boolean(tier.gifts?.length) && tier.gifts.every(
            (gift) => gift.variantId && gift.available,
          );
          if (Object.prototype.hasOwnProperty.call(this.tierAvailability, tier.index)) {
            tier.available = tier.available && this.tierAvailability[tier.index];
          }
        }
      }

      /**
       * @param {object} gift
       * @returns {string}
       */
      giftHandle(gift) {
        if (gift?.handle) return String(gift.handle);
        const url = String(gift?.url || '');
        const match = url.match(/\/products\/([^/?#]+)/);
        return match ? match[1] : '';
      }

      /**
       * @param {boolean} busy
       */
      setCheckoutBusy(busy) {
        const nodes = document.querySelectorAll(
          '.cart__checkout-button, .cart__shop-pay-button, .pf06-checkout-text, [name="checkout"]',
        );

        for (const node of nodes) {
          if (node instanceof HTMLButtonElement) {
            node.disabled = busy;
            continue;
          }

          if (node instanceof HTMLElement) {
            node.style.pointerEvents = busy ? 'none' : '';
          }
        }
      }

      /**
       * Re-fire the original control so Loop still sees .cart__checkout-button.
       * @param {{ kind: 'checkout' | 'shop_pay', node: Element, href?: string }} trigger
       */
      continueCheckout(trigger) {
        if (trigger.kind === 'shop_pay') {
          window.location.href = trigger.href || '/checkout?payment=shop_pay';
          return;
        }

        const button =
          document.querySelector('#CartDrawer-Checkout, #checkout.cart__checkout-button, .cart__checkout-button') ||
          (trigger.node instanceof HTMLElement && trigger.node.isConnected ? trigger.node : null);

        if (button instanceof HTMLElement) {
          button.click();
        } else {
          window.location.href = '/checkout';
        }

        window.setTimeout(() => {
          this.checkoutPassThrough = false;
        }, 0);
      }

      /**
       * @param {object} [cartState]
       * @param {{ skipRender?: boolean, forceStatus?: string }} [options]
       * @returns {Promise<object>}
       */
      async runSync(cartState, options = {}) {
        this.config = this.readConfig();
        if (this.config.enabled) {
          await this.ensureLiveAvailability();
        }

        let cart = this.isFullCart(cartState) ? cartState : await this.fetchCart();
        let plan = this.planSync(cart, options);
        this.lastPlan = plan;

        if (!plan.needsMutation) {
          this.publishChange(cart, plan);
          return cart;
        }

        this.syncing = true;
        this.quietRivoEcho();

        try {
          let nextState;

          try {
            nextState = await this.applyPlan(cart, plan);
          } catch (error) {
            nextState = await this.retryAfterAddFailure(error, options);
            plan = this.lastPlan || plan;
          }

          this.quietRivoEcho();
          this.publishChange(nextState, plan);

          if (options.skipRender !== true && nextState?.sections) {
            this.refreshCartUI(nextState);
          }

          return nextState;
        } catch (error) {
          console.error('GWP: sync failed', error);
          try {
            return await this.fetchCart();
          } catch {
            return cart;
          }
        } finally {
          this.syncing = false;
        }
      }

      /**
       * Recheck stock after a rejected batch and walk down only for stock
       * failures. Other cart errors must not poison inventory availability.
       * @param {Error & { tierIndex?: number, status?: number, stockFailure?: boolean }} error
       * @param {object} options
       * @returns {Promise<object>}
       */
      async retryAfterAddFailure(error, options = {}) {
        if (error?.status !== 422) throw error;

        await this.refreshTierAvailability();
        if (error.stockFailure) {
          this.tierAvailability[error.tierIndex] = false;
          this.applyLiveAvailability();
        }

        const cart = await this.fetchCart();
        const plan = this.planSync(cart, options);
        this.lastPlan = plan;
        if (plan.fulfillable === error.tierIndex) throw error;
        if (!plan.needsMutation) return cart;

        return this.applyPlan(cart, plan);
      }

      /**
       * @param {object} cart
       * @param {{ forceStatus?: string }} [options]
       */
      planSync(cart, options = {}) {
        const attributes = cart.attributes || {};
        let status = this.normalizeStatus(options.forceStatus || attributes._gwp_status || this.config.status);
        let acceptedTier = Number(attributes._gwp_accepted_tier ?? this.config.acceptedTier) || 0;
        const seenTiers = this.readSeenTiers(attributes._gwp_seen_tiers, this.config.seenTiers);
        const qualifying = this.computeQualifyingSubtotal(cart);
        const earned = this.resolveEarnedTier(cart, qualifying);
        const fulfillable = this.resolveFulfillableTier(earned, this.config.tiers);
        const giftLines = (cart.items || []).filter((item) => this.isGwpLine(item));
        const targetTier = (this.config.tiers || []).find((tier) => tier.index === fulfillable);
        const targetGifts = targetTier?.gifts || [];
        let removedVariants = this.parseSeen(
          this.readSeenTiers(attributes._gwp_removed_variants, this.config.removedVariants),
        );
        const removeKeys = [];
        const quantityFixKeys = [];
        const propertyFixes = [];
        const addVariantIds = [];
        let shouldPrompt = false;

        if (!this.config.enabled || fulfillable < 1) {
          status = 'none';
          acceptedTier = 0;
          removedVariants = [];
          const resetSeen = '';
          return {
            qualifying,
            earned,
            fulfillable,
            status,
            acceptedTier,
            seenTiers: resetSeen,
            removedVariants,
            needsMutation: giftLines.length > 0 || Boolean(attributes._gwp_checkout_restore) ||
              this.hasDirtyAttributes(attributes, status, 0, resetSeen, ''),
            removeKeys: giftLines.map((item) => item.key),
            addVariantIds,
            shouldPrompt: false,
            attributes: {
              ...this.attributePayload(status, 0, resetSeen, ''),
              ...(attributes._gwp_checkout_restore ? { _gwp_checkout_restore: '' } : {}),
            },
          };
        }

        if (options.forceStatus || acceptedTier !== fulfillable) {
          removedVariants = [];
        }

        if (status === 'declined') {
          acceptedTier = 0;
          removedVariants = [];
          removeKeys.push(...giftLines.map((item) => item.key));
        } else if (status === 'accepted' || (status !== 'removed' && giftLines.length > 0)) {
          if (status === 'accepted' && acceptedTier === fulfillable && !options.forceStatus && targetTier.available) {
            for (const gift of targetGifts) {
              if (!giftLines.some((item) => Number(item.id) === Number(gift.variantId))) {
                removedVariants.push(Number(gift.variantId));
              }
            }
          }

          const targetIds = new Set(targetGifts.map((gift) => Number(gift.variantId)));
          removedVariants = [...new Set(removedVariants)].filter((id) => targetIds.has(id));
          if (
            removedVariants.length > 0 &&
            !(this.config.tiers || []).some((tier) => tier.active && tier.thresholdCents > 0 && tier.index > fulfillable)
          ) {
            // The highest tier is accepted or removed as one complete gift set.
            removedVariants = [...targetIds];
          }
          const omitted = new Set(removedVariants);
          const kept = new Set();

          for (const item of giftLines) {
            const id = Number(item.id);
            if (!targetIds.has(id) || omitted.has(id) || kept.has(id)) {
              removeKeys.push(item.key);
              continue;
            }

            kept.add(id);
            if (item.quantity !== 1) quantityFixKeys.push(item.key);
            if (
              String(item.properties?._gwp_tier || '') !== String(fulfillable) ||
              item.properties?._gwp !== 'true' ||
              Object.hasOwn(item.properties || {}, 'Free Gift')
            ) {
              propertyFixes.push({
                key: item.key,
                properties: {
                  ...Object.fromEntries(Object.entries(item.properties || {}).filter(([key]) => key !== 'Free Gift')),
                  _gwp: 'true',
                  _gwp_tier: String(fulfillable),
                },
              });
            }
          }

          for (const id of targetIds) {
            if (!omitted.has(id) && !kept.has(id)) addVariantIds.push(id);
          }

          status = omitted.size === targetIds.size ? 'removed' : 'accepted';
          acceptedTier = fulfillable;
        } else {
          removeKeys.push(...giftLines.map((item) => item.key));
          if (status !== 'removed') acceptedTier = 0;
          shouldPrompt = status === 'none' && this.parseSeen(seenTiers).length === 0;
        }

        const removed = this.joinSeen(removedVariants);
        // Explicit shopper intent replaces any selection saved by checkout's automatic downgrade.
        const clearCheckoutRestore = attributes._gwp_checkout_restore &&
          (options.forceStatus || status !== 'accepted' || removedVariants.length > 0);
        return {
          qualifying,
          earned,
          fulfillable,
          status,
          acceptedTier,
          seenTiers,
          removedVariants,
          needsMutation:
            Boolean(clearCheckoutRestore) ||
            removeKeys.length > 0 ||
            quantityFixKeys.length > 0 ||
            propertyFixes.length > 0 ||
            addVariantIds.length > 0 ||
            this.hasDirtyAttributes(attributes, status, acceptedTier, seenTiers, removed),
          removeKeys,
          quantityFixKeys,
          propertyFixes,
          addVariantIds,
          shouldPrompt,
          attributes: {
            ...this.attributePayload(status, acceptedTier, seenTiers, removed),
            ...(clearCheckoutRestore ? { _gwp_checkout_restore: '' } : {}),
          },
          targetTier,
        };
      }

      /**
       * Persist a seen tier only after the offer modal is actually shown.
       * Planning the prompt must not write this, or a missed first event never retries.
       * @param {number} tier
       */
      markTierSeen(tier) {
        const index = Number(tier) || 0;
        if (index < 1) return Promise.resolve();

        return this.enqueue(async () => {
          const cart = await this.fetchCart();
          const current = this.readSeenTiers(cart.attributes?._gwp_seen_tiers);
          const nextSeen = this.joinSeen([...this.parseSeen(current), index]);
          if (this.joinSeen(this.parseSeen(current)) === nextSeen) return cart;

          this.syncing = true;
          this.quietRivoEcho();

          try {
            const status = this.normalizeStatus(cart.attributes?._gwp_status);
            const acceptedTier = Number(cart.attributes?._gwp_accepted_tier) || 0;
            return await this.postCartUpdate({
              attributes: this.attributePayload(status, acceptedTier, nextSeen, cart.attributes?._gwp_removed_variants),
            });
          } catch (error) {
            console.error('GWP: failed to persist seen tier', error);
            return cart;
          } finally {
            this.syncing = false;
          }
        });
      }

      /**
       * @param {object} cart
       * @param {object} plan
       * @returns {Promise<object>}
       */
      async applyPlan(cart, plan) {
        const sections = this.getSectionIds();
        const updates = {};

        for (const key of plan.removeKeys || []) updates[key] = 0;
        for (const key of plan.quantityFixKeys || []) updates[key] = 1;
        if (Object.keys(updates).length) await this.postCartUpdate({ updates });

        for (const fix of plan.propertyFixes || []) {
          await this.postCartChange({
            id: fix.key,
            quantity: 1,
            properties: fix.properties,
          });
        }

        if (plan.addVariantIds.length) {
          await this.postCartAdd(plan.addVariantIds.map((id) => ({
            id,
            quantity: 1,
            properties: {
              _gwp: 'true',
              _gwp_tier: String(plan.fulfillable),
            },
          })), plan.fulfillable);
        }

        const next = await this.fetchCart();
        const omitted = new Set(plan.removedVariants || []);
        const expected = plan.status === 'accepted'
          ? (plan.targetTier?.gifts || []).filter((gift) => !omitted.has(Number(gift.variantId)))
          : [];
        if (!this.hasGiftSet(next, expected, plan.fulfillable)) {
          throw new Error('GWP: requested gift set did not land');
        }

        return this.postCartUpdate({ attributes: plan.attributes, sections });
      }

      hasGiftSet(cart, gifts, tierIndex) {
        const lines = (cart.items || []).filter((item) => this.isGwpLine(item));
        const ids = new Set(gifts.map((gift) => Number(gift.variantId)));
        if (lines.length !== ids.size) return false;

        for (const line of lines) {
          const id = Number(line.id);
          if (!ids.delete(id) || line.quantity !== 1 || String(line.properties?._gwp_tier) !== String(tierIndex)) {
            return false;
          }
        }

        return ids.size === 0;
      }

      hasAcceptedGiftSet(cart) {
        const tier = this.lastPlan?.targetTier;
        return Boolean(
          tier?.gifts?.length &&
          this.lastPlan.status === 'accepted' &&
          cart?.attributes?._gwp_status === 'accepted' &&
          Number(cart.attributes._gwp_accepted_tier) === tier.index &&
          this.hasGiftSet(cart, tier.gifts, tier.index),
        );
      }

      /**
       * Hidden line marker. New lines write "true"; "1" still matches
       * leftover carts from the previous marker.
       * @param {object} item
       */
      isGwpLine(item) {
        const value = item?.properties?._gwp;
        return value === 'true' || value === '1' || value === true;
      }

      computeQualifyingSubtotal(cart) {
        let subtotal = 0;
        let excluded = 0;

        for (const item of cart.items || []) {
          const finalLinePrice = Number(item.final_line_price) || 0;
          subtotal += finalLinePrice;
          if (!this.isGwpLine(item) && item.product_type !== 'FREEGIFT_HIDDEN' && !item.gift_card) continue;

          // Ajax line_price includes allocated order discounts; final_line_price
          // includes line discounts only. Never exclude more than the line costs.
          excluded += Math.min(finalLinePrice, Number(item.line_price ?? finalLinePrice));
        }

        // Full Ajax carts supply the net total. Retain support for cart events
        // without totals by applying only their order-level allocations once.
        const orderDiscount = (cart.cart_level_discount_applications || []).reduce(
          (total, discount) => total + (Number(discount.total_allocated_amount) || 0),
          0,
        );
        return Math.max(0, Number(cart.total_price ?? (subtotal - orderDiscount)) - excluded);
      }

      resolveEarnedTier(cart, qualifying = this.computeQualifyingSubtotal(cart)) {
        let earned = 0;

        for (const tier of this.config.tiers || []) {
          if (!tier.active || !tier.thresholdCents) continue;
          if (qualifying >= tier.thresholdCents) {
            earned = tier.index;
          }
        }

        return earned;
      }

      resolveFulfillableTier(earned, tiers) {
        for (let index = earned; index >= 1; index -= 1) {
          const tier = (tiers || []).find((entry) => entry.index === index);
          if (tier?.active && tier.gifts?.length && tier.gifts.every((gift) => gift.variantId) && tier.available) {
            return index;
          }
        }

        return 0;
      }

      normalizeStatus(value) {
        const allowed = ['none', 'accepted', 'declined', 'removed'];
        return allowed.includes(value) ? value : 'none';
      }

      parseSeen(value) {
        return String(value || '')
          .split(',')
          .map((entry) => Number(entry))
          .filter((entry) => entry > 0);
      }

      joinSeen(list) {
        return [...new Set(list)]
          .filter((entry) => entry > 0)
          .sort((left, right) => left - right)
          .join(',');
      }

      /**
       * Shopify cart/update.js ignores empty-string attributes, so a cleared
       * seen list is stored as "0". Prefer the live cart value over Liquid
       * config — config is from page load and goes stale after an in-page reset.
       * @param {string} [attributeValue]
       * @param {string} [fallback]
       */
      readSeenTiers(attributeValue, fallback = '') {
        if (attributeValue == null || attributeValue === '') {
          return String(fallback || '');
        }

        return String(attributeValue);
      }

      attributePayload(status, acceptedTier, seenTiers, removedVariants = '') {
        return {
          _gwp_status: status,
          _gwp_accepted_tier: String(acceptedTier || 0),
          _gwp_seen_tiers: this.joinSeen(this.parseSeen(seenTiers)) || '0',
          _gwp_removed_variants: this.joinSeen(this.parseSeen(removedVariants)) || '0',
        };
      }

      hasDirtyAttributes(attributes, status, acceptedTier, seenTiers, removedVariants = '') {
        return (
          String(attributes._gwp_status || 'none') !== status ||
          String(attributes._gwp_accepted_tier || '0') !== String(acceptedTier || 0) ||
          this.joinSeen(this.parseSeen(attributes._gwp_seen_tiers)) !== this.joinSeen(this.parseSeen(seenTiers)) ||
          this.joinSeen(this.parseSeen(attributes._gwp_removed_variants)) !== this.joinSeen(this.parseSeen(removedVariants))
        );
      }

      /**
       * /cart/add.js returns `{ items }` for the added lines only — no token,
       * no attributes. Using that as the cart hides `_gwp_status` / seen tiers
       * and can compute the wrong qualifying subtotal.
       * @param {object} [state]
       */
      isFullCart(state) {
        return Boolean(state?.items && state.token);
      }

      async fetchCart() {
        const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`);
        if (!response.ok) {
          throw new Error(`GWP cart fetch failed: ${response.status}`);
        }

        return response.json();
      }

      async postCartUpdate(payload) {
        const body = { ...payload };
        if (body.sections) {
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
          ...fetchConfig(),
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error(`GWP cart update failed: ${response.status}`);
        }

        return response.json();
      }

      async postCartChange(payload) {
        const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
          ...fetchConfig(),
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`GWP cart change failed: ${response.status}`);
        }

        return response.json();
      }

      async postCartAdd(items, tierIndex) {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          ...fetchConfig(),
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const error = new Error(`GWP cart add failed: ${response.status}`);
          error.status = response.status;
          error.tierIndex = tierIndex;
          if (response.status === 422) {
            const detail = await response.json().catch(() => ({}));
            error.stockFailure = /sold out|out of stock|not enough|only .* available|can't add more|cannot add more|inventory/i.test(
              `${detail.message || ''} ${detail.description || ''}`,
            );
          }
          throw error;
        }

        return response.json();
      }

      getSectionIds() {
        const cartItems = document.querySelector('cart-drawer-items, cart-items');
        if (typeof cartItems?.getSectionsToRender === 'function') {
          return cartItems.getSectionsToRender().map((section) => section.section || section.id);
        }

        if (document.querySelector('cart-drawer')) {
          return ['cart-drawer', 'cart-icon-bubble'];
        }

        return ['main-cart-items', 'cart-icon-bubble'];
      }

      refreshCartUI(state) {
        if (!state?.sections) return;

        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(state);
          return;
        }

        const cartItems = document.querySelector('cart-items');
        if (cartItems && typeof cartItems.renderContents === 'function') {
          cartItems.renderContents(state);
        }
      }

      highlightGiftUpgrade(tier) {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        for (const row of document.querySelectorAll(`[data-gwp-gift-tier="${tier}"]`)) {
          row.classList.add('is-gwp-upgrading');
          row.addEventListener('animationend', () => row.classList.remove('is-gwp-upgrading'), { once: true });
        }
      }

      publishChange(cart, plan) {
        const upgraded = this.lastPublishedTier !== null &&
          plan.fulfillable > this.lastPublishedTier && plan.status === 'accepted';
        this.lastPublishedTier = plan.fulfillable;
        const detail = {
          enabled: Boolean(this.config.enabled),
          qualifyingSubtotal: plan.qualifying,
          earnedTier: plan.earned,
          fulfillableTier: plan.fulfillable,
          status: plan.status,
          acceptedTier: plan.acceptedTier,
          copy: this.config.copy,
          tiers: this.config.tiers,
          atcVisible: plan.fulfillable >= 1 && (
            plan.status === 'removed' || plan.status === 'declined' || plan.removedVariants?.length > 0
          ),
          shouldPrompt: Boolean(plan.shouldPrompt),
          giftName: plan.targetTier?.name || '',
          giftValue: plan.targetTier?.valueText || '',
          giftImage: plan.targetTier?.image || '',
        };

        this.dispatchEvent(new CustomEvent('gwp:change', { bubbles: true, detail }));
        document.dispatchEvent(new CustomEvent('gwp:change', { detail }));

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.gwpChange) {
          publish(PUB_SUB_EVENTS.gwpChange, detail);
        }

        if (upgraded) {
          window.requestAnimationFrame(() => this.highlightGiftUpgrade(plan.fulfillable));
        }
      }
    }
  );
}

if (!customElements.get('gwp-progress')) {
  customElements.define(
    'gwp-progress',
    class GwpProgress extends HTMLElement {
      connectedCallback() {
        this.onDetails = this.onDetails.bind(this);
        this.onAccept = this.onAccept.bind(this);
        this.onGwpChange = this.onGwpChange.bind(this);
        this.querySelector('[data-gwp-details]')?.addEventListener('click', this.onDetails);
        this.querySelector('[data-gwp-accept]')?.addEventListener('click', this.onAccept);
        document.addEventListener('gwp:change', this.onGwpChange);
      }

      disconnectedCallback() {
        this.querySelector('[data-gwp-details]')?.removeEventListener('click', this.onDetails);
        this.querySelector('[data-gwp-accept]')?.removeEventListener('click', this.onAccept);
        document.removeEventListener('gwp:change', this.onGwpChange);
      }

      onGwpChange(event) {
        this.render(event.detail || {});
      }

      render(detail) {
        const variant = this.dataset.variant;
        const earned = Number(detail.earnedTier) || 0;
        const fulfillable = Number(detail.fulfillableTier) || 0;
        const qualifying = Number(detail.qualifyingSubtotal) || 0;
        const tiers = detail.tiers || [];
        this.dataset.earned = String(earned);
        this.dataset.fulfillable = String(fulfillable);
        this.dataset.status = detail.status || 'none';

        const shortHeadline = this.querySelector('[data-gwp-headline]');
        const longHeadline = this.querySelector('[data-gwp-headline-long]');
        const spendHeadline = this.buildSpendHeadline(detail);
        if (shortHeadline) {
          shortHeadline.textContent = spendHeadline;
        }
        if (longHeadline) {
          longHeadline.textContent = spendHeadline;
        }

        const unlockedLine = this.querySelector('[data-gwp-unlocked-line]');
        if (unlockedLine) {
          const tier = tiers.find((entry) => entry.index === fulfillable);
          unlockedLine.hidden = fulfillable < 1;
          if (tier?.name) {
            unlockedLine.textContent = `You've Unlocked the ${tier.name}`;
          }
        }

        const marks = [...this.querySelectorAll('[data-gwp-mark]')];
        const lastThreshold = Number(marks[marks.length - 1]?.dataset.gwpThresholdCents) || 0;
        const atMax = lastThreshold > 0 && qualifying >= lastThreshold;

        for (const mark of marks) {
          const index = Number(mark.dataset.gwpMark);
          mark.classList.toggle('is-current', earned === index);
          mark.classList.toggle('is-complete', atMax);
        }

        const bar = this.querySelector('[role="progressbar"]');
        if (bar) {
          const progress = Math.max(0, Math.min(qualifying, lastThreshold));
          bar.setAttribute('aria-valuemax', String(lastThreshold));
          bar.setAttribute('aria-valuenow', String(progress));
          bar.setAttribute('aria-valuetext', `${this.formatMoney(progress)} of ${this.formatMoney(lastThreshold)} qualifying spend`);
        }

        const fillNode = this.querySelector('[data-gwp-fill]');
        if (fillNode) {
          let fillStart = 0;
          let fillWidth = 0;

          if (atMax) {
            fillWidth = 100;
          } else {
            const nextIndex = marks.findIndex((mark) => Number(mark.dataset.gwpThresholdCents) > qualifying);
            if (nextIndex >= 0) {
              const previous = marks[nextIndex - 1];
              const previousThreshold = Number(previous?.dataset.gwpThresholdCents) || 0;
              const nextThreshold = Number(marks[nextIndex].dataset.gwpThresholdCents);
              fillStart = Number.parseFloat(previous?.style.left) || 0;
              const fillEnd = Number.parseFloat(marks[nextIndex].style.left) || 0;
              const fraction = Math.max(0, Math.min(1, (qualifying - previousThreshold) / (nextThreshold - previousThreshold)));
              fillWidth = (fillEnd - fillStart) * fraction;
            }
          }

          fillNode.style.left = `${fillStart}%`;
          fillNode.style.width = `${fillWidth}%`;
        }

        for (const gift of this.querySelectorAll('[data-gwp-gift]')) {
          const index = Number(gift.dataset.gwpGift);
          gift.classList.toggle('is-current', fulfillable === index);
          gift.classList.toggle('is-complete', fulfillable > index);
          const pill = gift.querySelector('.gwp-progress__gift-pill');
          if (pill) pill.hidden = fulfillable !== index;
        }

        const current = this.querySelector('[data-gwp-current]');
        if (current) {
          const tier = tiers.find((entry) => entry.index === fulfillable);
          current.hidden = fulfillable < 1;
          const nameNode = current.querySelector('[data-gwp-current-name]');
          const valueNode = current.querySelector('[data-gwp-current-value]');
          if (nameNode) nameNode.textContent = tier?.name || '';
          if (valueNode) {
            valueNode.textContent = tier?.valueText || '';
            valueNode.hidden = !tier?.valueText;
          }
        }

        const nudge = this.querySelector('[data-gwp-nudge]');
        if (nudge) {
          const remaining = this.nextRemainingCents(tiers, qualifying);
          nudge.hidden = earned >= 1 || remaining <= 0;
          if (!nudge.hidden) {
            nudge.textContent = `Spend ${this.formatMoney(remaining)} more to unlock your first gift.`;
          }
        }

        const atc = this.querySelector('[data-gwp-accept]');
        if (atc) {
          atc.hidden = variant !== 'cart' || !detail.atcVisible;
        }
      }

      /**
       * Progress headline for cart, banner, and widget.
       * completed = how many active thresholds are met (not the tier index).
       */
      buildSpendHeadline(detail) {
        const copy = detail.copy || {};
        const tiers = this.activeTiers(detail.tiers || []);
        const qualifying = Number(detail.qualifyingSubtotal) || 0;
        const n = tiers.length;
        let completed = 0;

        for (const tier of tiers) {
          if (qualifying >= Number(tier.thresholdCents)) {
            completed += 1;
          }
        }

        const unlock = copy.headlineUnlock || copy.headline || 'Unlock a Free Gift when you spend $$$';
        const upgrade = copy.headlineUpgrade || 'Upgrade Your Gift! Just Spend $$$ More';
        const top = copy.headlineTop || 'Spend $$$ More for the Top Level!';
        const complete = copy.headlineComplete || 'Congratulations! You\'ve unlocked all gifts';

        if (n < 1) {
          return this.replaceMoney(unlock, '');
        }

        const first = tiers[0];
        const last = tiers[n - 1];
        const next = this.nextTier(tiers, qualifying);
        const remainingNext = next ? Math.max(0, Number(next.thresholdCents) - qualifying) : 0;
        const remainingLast = Math.max(0, Number(last.thresholdCents) - qualifying);

        if (completed < 1) {
          return this.replaceMoney(unlock, this.formatMoney(first.thresholdCents));
        }

        if (completed >= n) {
          return complete;
        }

        if (completed === n - 1 || n < 4) {
          return this.replaceMoney(top, this.formatMoney(remainingLast));
        }

        return this.replaceMoney(upgrade, this.formatMoney(remainingNext));
      }

      activeTiers(tiers) {
        const list = [];

        for (const tier of tiers) {
          if (tier.active && Number(tier.thresholdCents) > 0) {
            list.push(tier);
          }
        }

        return list;
      }

      replaceMoney(template, money) {
        return String(template || '').replace(/\$\$\$/g, money);
      }

      lastThresholdCents(tiers) {
        let last = 0;
        for (const tier of tiers) {
          if (tier.active && tier.thresholdCents) {
            last = Number(tier.thresholdCents);
          }
        }
        return last;
      }

      nextTier(tiers, qualifying) {
        for (const tier of tiers) {
          if (tier.active && Number(tier.thresholdCents) > qualifying) {
            return tier;
          }
        }
        return undefined;
      }

      nextRemainingCents(tiers, qualifying) {
        const tier = this.nextTier(tiers, qualifying);
        if (!tier) return 0;
        return Math.max(0, Number(tier.thresholdCents) - qualifying);
      }

      formatMoney(cents) {
        const amount = Number(cents) || 0;

        if (typeof window.formatMoney === 'function' && window.moneyFormat) {
          let formatted = window.formatMoney(amount, window.moneyFormat).replace(/\.00$/, '');

          if (window.currencyCodeEnabled && window.cartCurrency?.code) {
            formatted = `${formatted} ${window.cartCurrency.code}`;
          }

          return formatted;
        }

        const dollars = Math.round(amount) / 100;
        return `$${dollars.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
      }

      onDetails() {
        document.querySelector('gwp-details-modal')?.open(Number(this.dataset.fulfillable) || 0);
      }

      onAccept() {
        document.querySelector('gwp-offer-modal')?.open();
      }
    }
  );
}

if (!customElements.get('gwp-details-modal')) {
  customElements.define(
    'gwp-details-modal',
    class GwpDetailsModal extends HTMLElement {
      connectedCallback() {
        this.onClose = this.onClose.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.querySelectorAll('[data-gwp-close]').forEach((node) => {
          node.addEventListener('click', this.onClose);
        });
      }

      disconnectedCallback() {
        this.querySelectorAll('[data-gwp-close]').forEach((node) => {
          node.removeEventListener('click', this.onClose);
        });
        document.removeEventListener('keydown', this.onKeydown);
      }

      /**
       * @param {number} earnedTier
       */
      open(earnedTier) {
        this.hidden = false;
        this.classList.add('is-open');
        this.querySelectorAll('[data-gwp-tier]').forEach((tier) => {
          const isCurrent = Number(tier.dataset.gwpTier) === Number(earnedTier);
          tier.classList.toggle('is-current', isCurrent);
          const pill = tier.querySelector('.gwp-details__current-pill');
          if (pill) pill.hidden = !isCurrent;
        });
        document.body.classList.add('gwp-modal-open');
        document.addEventListener('keydown', this.onKeydown);
        this.querySelector('.gwp-details__close')?.focus();
      }

      close() {
        this.classList.remove('is-open');
        this.hidden = true;
        document.body.classList.remove('gwp-modal-open');
        document.removeEventListener('keydown', this.onKeydown);
      }

      onClose() {
        this.close();
      }

      onKeydown(event) {
        if (event.key === 'Escape') this.close();
      }
    }
  );
}

if (!customElements.get('gwp-offer-modal')) {
  customElements.define(
    'gwp-offer-modal',
    class GwpOfferModal extends HTMLElement {
      connectedCallback() {
        this.onAccept = this.onAccept.bind(this);
        this.onDecline = this.onDecline.bind(this);
        this.onDismiss = this.onDismiss.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onGwpChange = this.onGwpChange.bind(this);
        this.querySelector('[data-gwp-accept]')?.addEventListener('click', this.onAccept);
        this.querySelector('[data-gwp-decline]')?.addEventListener('click', this.onDecline);
        this.querySelectorAll('[data-gwp-dismiss]').forEach((node) => {
          node.addEventListener('click', this.onDismiss);
        });
        document.addEventListener('gwp:change', this.onGwpChange);
        queueMicrotask(() => this.catchUp());
      }

      catchUp() {
        const plan = document.querySelector('gwp-controller')?.lastPlan;
        if (plan?.shouldPrompt && plan.status === 'none' && !this.classList.contains('is-open')) {
          this.open({
            giftName: plan.targetTier?.name || '',
            giftValue: plan.targetTier?.valueText || '',
            fulfillableTier: plan.fulfillable,
            status: plan.status,
          });
        }
      }

      disconnectedCallback() {
        this.querySelector('[data-gwp-accept]')?.removeEventListener('click', this.onAccept);
        this.querySelector('[data-gwp-decline]')?.removeEventListener('click', this.onDecline);
        this.querySelectorAll('[data-gwp-dismiss]').forEach((node) => {
          node.removeEventListener('click', this.onDismiss);
        });
        document.removeEventListener('gwp:change', this.onGwpChange);
        document.removeEventListener('keydown', this.onKeydown);
        document.body.classList.remove('gwp-modal-open');
      }

      onGwpChange(event) {
        const detail = event.detail || {};
        this.lastDetail = detail;

        if (this.classList.contains('is-open')) {
          if (Number(detail.fulfillableTier) < 1) this.close();
          else this.fillGift(detail);
          return;
        }

        if (detail.shouldPrompt && detail.status === 'none' && !this.classList.contains('is-open')) {
          this.open(detail);
        }
      }

      /**
       * @param {object} [detail]
       */
      open(detail = this.lastDetail || {}) {
        if (!detail.giftName) {
          const controller = document.querySelector('gwp-controller');
          const config = controller?.config || {};
          const fulfillable = Number(controller?.lastPlan?.fulfillable) || 0;
          const tier = (config.tiers || []).find((entry) => entry.index === fulfillable);
          detail = {
            ...detail,
            fulfillableTier: fulfillable,
            giftName: tier?.name || '',
            giftValue: tier?.valueText || '',
            giftImage: detail.giftImage || tier?.image || '',
          };
        }

        this.fillGift(detail);
        this.hidden = false;
        this.classList.add('is-open');
        document.body.classList.add('gwp-modal-open');
        document.addEventListener('keydown', this.onKeydown);
        this.querySelector('[data-gwp-accept]')?.focus();

        const seenTier = Number(detail.fulfillableTier || document.querySelector('gwp-controller')?.lastPlan?.fulfillable) || 0;
        document.querySelector('gwp-controller')?.markTierSeen(seenTier);
      }

      close() {
        this.classList.remove('is-open');
        this.hidden = true;
        document.body.classList.remove('gwp-modal-open');
        document.removeEventListener('keydown', this.onKeydown);
      }

      fillGift(detail) {
        const controller = document.querySelector('gwp-controller');
        const fulfillable = Number(detail.fulfillableTier || controller?.lastPlan?.fulfillable) || 0;
        const tier = (controller?.config?.tiers || []).find(
          (entry) => entry.index === fulfillable,
        );
        const name = detail.giftName || tier?.name || '';
        const value = detail.giftValue || tier?.valueText || '';
        const image = detail.giftImage || tier?.image || '';
        const nameNode = this.querySelector('[data-gwp-offer-name]');
        const valueNode = this.querySelector('[data-gwp-offer-value]');
        const mediaNode = this.querySelector('[data-gwp-offer-media]');
        const imageNode = this.querySelector('[data-gwp-offer-image]');

        if (nameNode) nameNode.textContent = name;
        if (valueNode) {
          valueNode.textContent = value;
          valueNode.hidden = !value;
        }

        if (mediaNode && imageNode) {
          if (image) {
            imageNode.src = image;
            imageNode.alt = name;
            mediaNode.hidden = false;
          } else {
            imageNode.removeAttribute('src');
            imageNode.alt = '';
            mediaNode.hidden = true;
          }
        }
      }

      async onAccept() {
        const controller = document.querySelector('gwp-controller');
        const button = this.querySelector('[data-gwp-accept]');
        button.disabled = true;

        try {
          await controller?.acceptGift();
          this.close();
        } finally {
          button.disabled = false;
        }
      }

      async onDecline() {
        const controller = document.querySelector('gwp-controller');
        await controller?.declineGift();
        this.close();
      }

      onDismiss() {
        this.close();
      }

      onKeydown(event) {
        if (event.key === 'Escape') this.close();
      }
    }
  );
}
